import { Direction, EventType, RelationType, RelationsEvent, RoomEvent } from 'matrix-js-sdk'
import type { MatrixClient, MatrixEvent, Room } from 'matrix-js-sdk'
import type { Relations } from 'matrix-js-sdk'
import type { IMessage, IMessageReaction, IReactionType } from '@/modules/chat-matrix-fast/types/IChat'
import { getReactionEmoji, stripEmojiKeyPrefix } from '@/modules/chat-matrix-fast/utils/reactionUtils'
import { toAuthenticatedMatrixMediaUrl } from '@/modules/chat-matrix-fast/services/matrixMediaUrl'

const DEFAULT_REACTION_TYPES: IReactionType[] = [
  { id: 1, name: 'Like', icon: '👍' },
  { id: 2, name: 'Love', icon: '❤️' },
  { id: 3, name: 'Laugh', icon: '😂' },
  { id: 4, name: 'Wow', icon: '😮' },
  { id: 5, name: 'Sad', icon: '😢' },
  { id: 6, name: 'Angry', icon: '😠' },
]

function hashToNumber(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(i)
  }

  return Math.abs(hash)
}

function normalizeEmojiForMatch(emoji: string): string {
  return stripEmojiKeyPrefix(emoji).replace(/\uFE0F/g, '').trim()
}

function dedupeReactionsPerUser(reactions: IMessageReaction[]): IMessageReaction[] {
  const byUser = new Map<string, IMessageReaction>()

  for (const reaction of reactions) {
    const userId = String(reaction.user || reaction.user_id || '')
    if (!userId) {
      continue
    }

    const existing = byUser.get(userId)
    if (!existing) {
      byUser.set(userId, reaction)
      continue
    }

    const existingTs = new Date(existing.created_at || 0).getTime()
    const currentTs = new Date(reaction.created_at || 0).getTime()
    if (currentTs >= existingTs) {
      byUser.set(userId, reaction)
    }
  }

  return Array.from(byUser.values())
}

function reactionsMatch(
  a: IMessageReaction | undefined,
  b: IMessageReaction | undefined,
): boolean {
  if (!a && !b) {
    return true
  }

  if (!a || !b) {
    return false
  }

  const aKey = a.matrix_emoji_key
    ? normalizeEmojiForMatch(a.matrix_emoji_key)
    : String(a.reaction_type_id ?? '')
  const bKey = b.matrix_emoji_key
    ? normalizeEmojiForMatch(b.matrix_emoji_key)
    : String(b.reaction_type_id ?? '')

  return aKey === bKey
}

export function mergeMessageReactionsFromLocal(
  incoming: IMessage,
  local: IMessage | undefined,
  matrixUserId: string,
  maxAgeMs = 15000,
): IMessage {
  const incomingReactions = [...(incoming.reactions || incoming.message_reactions || [])]
  const localReactions = [...(local?.reactions || local?.message_reactions || [])]

  // /relations уже в local, а live-timeline extract пустой — не затираем после reload
  if (incomingReactions.length === 0 && localReactions.length > 0) {
    return {
      ...incoming,
      reactions: localReactions,
      message_reactions: localReactions,
      reaction_updated_at: local?.reaction_updated_at,
    }
  }

  const localMyReaction = localReactions.find(
    reaction => String(reaction.user || reaction.user_id) === matrixUserId,
  )
  const incomingMyReaction = incomingReactions.find(
    reaction => String(reaction.user || reaction.user_id) === matrixUserId,
  )

  if (!local?.reaction_updated_at || !matrixUserId) {
    return incoming
  }

  const age = Date.now() - new Date(local.reaction_updated_at).getTime()
  if (age > maxAgeMs) {
    return incoming
  }

  if (reactionsMatch(incomingMyReaction, localMyReaction)) {
    return {
      ...incoming,
      reaction_updated_at: undefined,
    }
  }

  const othersReactions = incomingReactions.filter(
    reaction => String(reaction.user || reaction.user_id) !== matrixUserId,
  )

  if (!localMyReaction) {
    if (incomingMyReaction) {
      return {
        ...incoming,
        reaction_updated_at: undefined,
      }
    }

    return {
      ...incoming,
      reactions: othersReactions,
      message_reactions: othersReactions,
      reaction_updated_at: undefined,
    }
  }

  const merged = [...othersReactions, localMyReaction]
  return {
    ...incoming,
    reactions: merged,
    message_reactions: merged,
    reaction_updated_at: local.reaction_updated_at,
  }
}

export function applyPendingReactionOverride(
  message: IMessage,
  override: {
    matrixUserId: string
    reactions: IMessageReaction[]
    expectedEmoji: string | null
    updatedAt: string
  } | undefined,
  maxAgeMs = 15000,
): IMessage {
  if (!override?.matrixUserId) {
    return message
  }

  const age = Date.now() - new Date(override.updatedAt).getTime()
  if (age > maxAgeMs) {
    return message
  }

  const incomingReactions = [...(message.reactions || message.message_reactions || [])]
  const incomingMyReaction = incomingReactions.find(
    reaction => String(reaction.user || reaction.user_id) === override.matrixUserId,
  )

  if (override.reactions.length === 0) {
    if (!incomingMyReaction) {
      const othersReactions = incomingReactions.filter(
        reaction => String(reaction.user || reaction.user_id) !== override.matrixUserId,
      )

      return {
        ...message,
        reactions: othersReactions,
        message_reactions: othersReactions,
        reaction_updated_at: undefined,
      }
    }

    const othersReactions = incomingReactions.filter(
      reaction => String(reaction.user || reaction.user_id) !== override.matrixUserId,
    )

    return {
      ...message,
      reactions: othersReactions,
      message_reactions: othersReactions,
      reaction_updated_at: override.updatedAt,
    }
  }

  const pendingMyReaction = override.reactions.find(
    reaction => String(reaction.user || reaction.user_id) === override.matrixUserId,
  )

  if (pendingMyReaction && reactionsMatch(incomingMyReaction, pendingMyReaction)) {
    const othersReactions = incomingReactions.filter(
      reaction => String(reaction.user || reaction.user_id) !== override.matrixUserId,
    )
    const settledReaction = {
      ...(incomingMyReaction || pendingMyReaction),
      user_name: pendingMyReaction.user_name || incomingMyReaction?.user_name,
      avatar: pendingMyReaction.avatar ?? incomingMyReaction?.avatar ?? null,
    }

    return {
      ...message,
      reactions: [...othersReactions, settledReaction],
      message_reactions: [...othersReactions, settledReaction],
      reaction_updated_at: undefined,
    }
  }

  const othersReactions = incomingReactions.filter(
    reaction => String(reaction.user || reaction.user_id) !== override.matrixUserId,
  )
  const merged = pendingMyReaction
    ? [...othersReactions, pendingMyReaction]
    : othersReactions

  return {
    ...message,
    reactions: merged,
    message_reactions: merged,
    reaction_updated_at: override.updatedAt,
  }
}

function buildReactionFromEvent(
  event: MatrixEvent,
  emojiKey: string,
  matrixClient: MatrixClient,
  room: Room,
  reactionTypes: IReactionType[],
): IMessageReaction | null {
  if (event.isRedacted()) {
    return null
  }

  const senderId = event.getSender() ?? ''
  const eventId = event.getId()
  const member = room.getMember(senderId)
  const displayName = member?.name || member?.rawDisplayName || senderId
  const mxcAvatar = member?.getMxcAvatarUrl?.()
  const avatarUrl = mxcAvatar
    ? toAuthenticatedMatrixMediaUrl(matrixClient, mxcAvatar, 96, 96, 'crop') || null
    : null
  const cleanEmojiKey = stripEmojiKeyPrefix(emojiKey)
  const reactionTypeId = emojiToReactionTypeId(cleanEmojiKey, reactionTypes)

  return {
    id: hashToNumber(eventId ?? `${senderId}:${cleanEmojiKey}:${event.getTs()}`),
    reaction_type: reactionTypeId,
    reaction_type_id: reactionTypeId,
    matrix_emoji_key: cleanEmojiKey,
    user: senderId,
    user_id: senderId,
    user_name: displayName,
    avatar: avatarUrl,
    created_at: new Date(event.getTs()).toISOString(),
  }
}

export function reactionTypeIdToEmoji(
  reactionTypeId: number | string,
  reactionTypes: IReactionType[],
): string {
  const lookupTypes = reactionTypes.length > 0 ? reactionTypes : DEFAULT_REACTION_TYPES
  const numericId = Number(reactionTypeId)
  if (Number.isFinite(numericId)) {
    const match = lookupTypes.find(rt => rt.id === numericId)
      || DEFAULT_REACTION_TYPES.find(rt => rt.id === numericId)
    if (match) {
      return getReactionEmoji(match)
    }
  }

  const asString = stripEmojiKeyPrefix(String(reactionTypeId))
  if (asString && asString.length <= 8 && !/^\d+$/.test(asString) && !asString.includes(':')) {
    return asString
  }

  return '👍'
}

export function emojiToReactionTypeId(
  emoji: string,
  reactionTypes: IReactionType[],
): number {
  const lookupTypes = reactionTypes.length > 0 ? reactionTypes : DEFAULT_REACTION_TYPES
  const normalizedEmoji = normalizeEmojiForMatch(emoji)
  const match = lookupTypes.find(rt => {
    const icon = normalizeEmojiForMatch(String(rt.icon || ''))
    return normalizeEmojiForMatch(getReactionEmoji(rt)) === normalizedEmoji
      || icon === normalizedEmoji
  }) || DEFAULT_REACTION_TYPES.find(rt => {
    const icon = normalizeEmojiForMatch(String(rt.icon || ''))
    return normalizeEmojiForMatch(getReactionEmoji(rt)) === normalizedEmoji
      || icon === normalizedEmoji
  })

  if (match) {
    return match.id
  }

  return hashToNumber(normalizedEmoji)
}

function extractReactionsFromTimeline(
  matrixClient: MatrixClient,
  room: Room,
  matrixEventId: string,
  reactionTypes: IReactionType[],
): IMessageReaction[] {
  const reactions: IMessageReaction[] = []
  const timelineEvents = room.getLiveTimeline?.().getEvents?.() ?? room.timeline ?? []

  for (const event of timelineEvents) {
    if (event.getType() !== EventType.Reaction) {
      continue
    }

    const relation = event.getRelation()
    if (!relation || relation.event_id !== matrixEventId) {
      continue
    }

    const emojiKey = String(relation.key || '')
    if (!emojiKey) {
      continue
    }

    const reaction = buildReactionFromEvent(
      event,
      emojiKey,
      matrixClient,
      room,
      reactionTypes,
    )

    if (reaction) {
      reactions.push(reaction)
    }
  }

  return reactions
}

export function getReactionsForEvent(
  matrixClient: MatrixClient,
  room: Room,
  matrixEventId: string,
  reactionTypes: IReactionType[] = [],
  options?: { allowTimelineFallback?: boolean },
): IMessageReaction[] {
  ensureReactionsCacheInvalidation(room)

  const relations = room.relations.getChildEventsForEvent(
    matrixEventId,
    RelationType.Annotation,
    EventType.Reaction,
  )

  const reactions: IMessageReaction[] = []

  if (relations) {
    wireRelationsCacheInvalidation(room, relations, matrixEventId)
    const grouped = relations.getSortedAnnotationsByKey()
    if (grouped) {
      for (const [emojiKey, events] of grouped) {
        for (const event of events) {
          const reaction = buildReactionFromEvent(
            event,
            emojiKey,
            matrixClient,
            room,
            reactionTypes,
          )
          if (reaction) {
            reactions.push(reaction)
          }
        }
      }
    }
  }

  if (reactions.length > 0) {
    return dedupeReactionsPerUser(reactions)
  }

  if (!shouldAllowTimelineFallback(room.roomId, matrixEventId, options)) {
    return []
  }

  const fromTimeline = extractReactionsFromTimeline(
    matrixClient,
    room,
    matrixEventId,
    reactionTypes,
  )

  if (fromTimeline.length > 0) {
    return dedupeReactionsPerUser(fromTimeline)
  }

  return []
}

function shouldAllowTimelineFallback(
  roomId: string,
  matrixEventId: string,
  options?: { allowTimelineFallback?: boolean },
): boolean {
  if (options?.allowTimelineFallback === false) {
    return false
  }

  if (options?.allowTimelineFallback === true) {
    return true
  }

  return getReactionsServerCacheState(roomId, matrixEventId) === 'missing'
}

function extractReactionsFromRoom(
  matrixClient: MatrixClient,
  room: Room,
  matrixEventId: string,
  reactionTypes: IReactionType[] = [],
  options?: { allowTimelineFallback?: boolean },
): IMessageReaction[] {
  return getReactionsForEvent(
    matrixClient,
    room,
    matrixEventId,
    reactionTypes,
    options,
  )
}

export function enrichMessagesWithReactions(
  matrixClient: MatrixClient,
  room: Room,
  messages: IMessage[],
  reactionTypes: IReactionType[] = [],
): IMessage[] {
  return messages.map(message => {
    if (!message.matrix_event_id) {
      return message
    }

    const reactions = extractReactionsFromRoom(
      matrixClient,
      room,
      message.matrix_event_id,
      reactionTypes,
    )

    return {
      ...message,
      reactions,
      message_reactions: reactions,
    }
  })
}

const RELATIONS_FETCH_CONCURRENCY = 2
const RELATIONS_FETCH_MAX_MESSAGES = 6
const REACTIONS_SERVER_CACHE_TTL_MS = 60_000
const RELATIONS_PAGE_LIMIT = 50
const RELATIONS_MAX_PAGES = 200

export type ReactionsServerCacheState = 'missing' | 'loading' | 'empty' | 'ready'

type ReactionsServerCacheEntry = {
  state: ReactionsServerCacheState
  fetchedAt: number
  reactions?: IMessageReaction[]
}

const reactionsServerCache = new Map<string, ReactionsServerCacheEntry>()
const reactionsServerFetchInFlight = new Map<string, Promise<IMessageReaction[]>>()
const invalidationWiredRooms = new WeakSet<Room>()
const relationsInvalidationWired = new Set<string>()

function reactionsServerCacheKey(roomId: string, matrixEventId: string): string {
  return `${roomId}:${matrixEventId}`
}

function isReactionsServerCacheFresh(entry: ReactionsServerCacheEntry): boolean {
  return Date.now() - entry.fetchedAt < REACTIONS_SERVER_CACHE_TTL_MS
}

export function invalidateReactionsServerCache(roomId: string, matrixEventId?: string): void {
  if (matrixEventId) {
    const cacheKey = reactionsServerCacheKey(roomId, matrixEventId)
    reactionsServerCache.delete(cacheKey)
    reactionsServerFetchInFlight.delete(cacheKey)
    return
  }

  const prefix = `${roomId}:`
  for (const key of reactionsServerCache.keys()) {
    if (key.startsWith(prefix)) {
      reactionsServerCache.delete(key)
      reactionsServerFetchInFlight.delete(key)
    }
  }
}

function ensureReactionsCacheInvalidation(room: Room): void {
  if (invalidationWiredRooms.has(room)) {
    return
  }

  invalidationWiredRooms.add(room)

  room.on(RoomEvent.Timeline, (event: MatrixEvent, _room, toStartOfTimeline) => {
    if (toStartOfTimeline) {
      return
    }

    if (event.getType() !== EventType.Reaction) {
      return
    }

    const parentEventId = event.getRelation()?.event_id
    if (parentEventId) {
      invalidateReactionsServerCache(room.roomId, parentEventId)
    }
  })
}

function wireRelationsCacheInvalidation(
  room: Room,
  relations: Relations,
  parentEventId: string,
): void {
  const wireKey = reactionsServerCacheKey(room.roomId, parentEventId)
  if (relationsInvalidationWired.has(wireKey)) {
    return
  }

  relationsInvalidationWired.add(wireKey)

  const invalidate = () => {
    invalidateReactionsServerCache(room.roomId, parentEventId)
  }

  relations.on(RelationsEvent.Add, invalidate)
  relations.on(RelationsEvent.Remove, invalidate)
  relations.on(RelationsEvent.Redaction, invalidate)
}

export function getReactionsServerCacheState(
  roomId: string,
  matrixEventId: string,
): ReactionsServerCacheState {
  const entry = reactionsServerCache.get(reactionsServerCacheKey(roomId, matrixEventId))
  if (!entry) {
    return 'missing'
  }

  return entry.state
}

export function needsServerBackfill(message: IMessage, roomId: string): boolean {
  const matrixEventId = message.matrix_event_id
  if (!matrixEventId) {
    return false
  }

  const localReactions = message.reactions || message.message_reactions || []
  if (localReactions.length > 0) {
    return false
  }

  const cacheKey = reactionsServerCacheKey(roomId, matrixEventId)
  const entry = reactionsServerCache.get(cacheKey)
  if (!entry) {
    return true
  }

  if (entry.state === 'loading') {
    return false
  }

  if (!isReactionsServerCacheFresh(entry)) {
    return true
  }

  if (entry.state === 'ready' && (entry.reactions?.length ?? 0) > 0) {
    return true
  }

  return entry.state !== 'empty' && entry.state !== 'ready'
}

export async function fetchAllRelationsFromServer(
  matrixClient: MatrixClient,
  room: Room,
  matrixEventId: string,
  reactionTypes: IReactionType[] = [],
): Promise<IMessageReaction[]> {
  ensureReactionsCacheInvalidation(room)

  const cacheKey = reactionsServerCacheKey(room.roomId, matrixEventId)
  const cached = reactionsServerCache.get(cacheKey)
  if (
    cached
    && (cached.state === 'ready' || cached.state === 'empty')
    && isReactionsServerCacheFresh(cached)
  ) {
    return cached.reactions ?? []
  }

  const inFlight = reactionsServerFetchInFlight.get(cacheKey)
  if (inFlight) {
    return inFlight
  }

  reactionsServerCache.set(cacheKey, { state: 'loading', fetchedAt: Date.now() })

  const fetchPromise = (async (): Promise<IMessageReaction[]> => {
    try {
      const mapper = matrixClient.getEventMapper()
      const reactions: IMessageReaction[] = []
      let from: string | undefined

      for (let page = 0; page < RELATIONS_MAX_PAGES; page += 1) {
        const response = await matrixClient.fetchRelations(
          room.roomId,
          matrixEventId,
          RelationType.Annotation,
          EventType.Reaction,
          {
            from,
            limit: RELATIONS_PAGE_LIMIT,
            dir: Direction.Backward,
          },
        )
        const chunk = Array.isArray(response?.chunk) ? response.chunk : []
        for (const raw of chunk) {
          const event = mapper(raw)
          if (event.isRedacted()) {
            continue
          }

          const relation = event.getRelation()
          const rawContent = (raw as { content?: { 'm.relates_to'?: { key?: string } } }).content
          const emojiKey = String(relation?.key || rawContent?.['m.relates_to']?.key || '')
          if (!emojiKey) {
            continue
          }

          room.relations.aggregateChildEvent(event)

          const reaction = buildReactionFromEvent(
            event,
            emojiKey,
            matrixClient,
            room,
            reactionTypes,
          )
          if (reaction) {
            reactions.push(reaction)
          }
        }

        const nextBatch = response?.next_batch
        if (!nextBatch) {
          break
        }
        if (!chunk.length) {
          break
        }
        from = nextBatch
      }

      const deduped = dedupeReactionsPerUser(reactions)
      reactionsServerCache.set(cacheKey, {
        state: deduped.length ? 'ready' : 'empty',
        fetchedAt: Date.now(),
        reactions: deduped,
      })
      return deduped
    } catch {
      reactionsServerCache.delete(cacheKey)
      throw new Error('fetchAllRelationsFromServer failed')
    } finally {
      reactionsServerFetchInFlight.delete(cacheKey)
    }
  })()

  reactionsServerFetchInFlight.set(cacheKey, fetchPromise)

  try {
    return await fetchPromise
  } catch {
    return []
  }
}

async function fetchReactionsFromServer(
  matrixClient: MatrixClient,
  room: Room,
  matrixEventId: string,
  reactionTypes: IReactionType[] = [],
): Promise<IMessageReaction[]> {
  return fetchAllRelationsFromServer(matrixClient, room, matrixEventId, reactionTypes)
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  if (!items.length) {
    return []
  }

  const results = Array.from({ length: items.length }) as R[]
  let nextIndex = 0

  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex
      nextIndex += 1
      results[index] = await mapper(items[index], index)
    }
  }

  const poolSize = Math.min(concurrency, items.length)
  await Promise.all(Array.from({ length: poolSize }, () => worker()))
  return results
}

export async function enrichMessagesWithReactionsAsync(
  matrixClient: MatrixClient,
  room: Room,
  messages: IMessage[],
  reactionTypes: IReactionType[] = [],
): Promise<IMessage[]> {
  const localEnriched = enrichMessagesWithReactions(
    matrixClient,
    room,
    messages,
    reactionTypes,
  )

  ensureReactionsCacheInvalidation(room)

  const needsServerFetch = localEnriched
    .map((message, index) => ({ message, index }))
    .filter(({ message }) => needsServerBackfill(message, room.roomId))
    .reverse()
    .slice(0, RELATIONS_FETCH_MAX_MESSAGES)

  if (!needsServerFetch.length) {
    return localEnriched
  }

  const serverByIndex = new Map<number, IMessageReaction[]>()

  await mapWithConcurrency(
    needsServerFetch,
    RELATIONS_FETCH_CONCURRENCY,
    async ({ message, index }) => {
      const fromServer = await fetchReactionsFromServer(
        matrixClient,
        room,
        message.matrix_event_id!,
        reactionTypes,
      )
      serverByIndex.set(index, fromServer)
      return null
    },
  )

  if (!serverByIndex.size) {
    return localEnriched
  }

  return localEnriched.map((message, index) => {
    const fromServer = serverByIndex.get(index)
    if (!fromServer?.length) {
      return message
    }

    const merged = dedupeReactionsPerUser([
      ...(message.reactions || message.message_reactions || []),
      ...fromServer,
    ])

    return {
      ...message,
      reactions: merged,
      message_reactions: merged,
    }
  })
}

export async function sendMatrixReaction(
  matrixClient: MatrixClient,
  roomId: string,
  matrixEventId: string,
  emoji: string,
): Promise<string | undefined> {
  const key = stripEmojiKeyPrefix(emoji) || emoji
  const response = await matrixClient.sendEvent(roomId, EventType.Reaction, {
    'm.relates_to': {
      rel_type: RelationType.Annotation,
      event_id: matrixEventId,
      key,
    },
  })

  return (response as { event_id?: string } | undefined)?.event_id
}

function collectMyReactionEventsFromRelations(
  room: Room,
  matrixEventId: string,
  myMatrixUserId: string,
): MatrixEvent[] {
  const events: MatrixEvent[] = []

  const relations = room.relations.getChildEventsForEvent(
    matrixEventId,
    RelationType.Annotation,
    EventType.Reaction,
  )

  if (!relations) {
    return events
  }

  const bySender = relations.getAnnotationsBySender()
  const myEvents = bySender?.[myMatrixUserId]
  if (!myEvents?.size) {
    return events
  }

  for (const event of myEvents) {
    if (!event.isRedacted()) {
      events.push(event)
    }
  }

  return events
}

function collectMyReactionEventsFromTimeline(
  room: Room,
  matrixEventId: string,
  myMatrixUserId: string,
): MatrixEvent[] {
  const events: MatrixEvent[] = []
  const timelineEvents = room.getLiveTimeline?.().getEvents?.() ?? room.timeline ?? []

  for (const event of timelineEvents) {
    if (event.getType() !== EventType.Reaction || event.isRedacted()) {
      continue
    }

    const relation = event.getRelation()
    if (
      relation?.event_id === matrixEventId
      && event.getSender() === myMatrixUserId
    ) {
      events.push(event)
    }
  }

  return events
}

function collectMyReactionEvents(
  room: Room,
  matrixEventId: string,
  myMatrixUserId: string,
  options?: { allowTimelineFallback?: boolean },
): MatrixEvent[] {
  const eventsToRedact = new Set<MatrixEvent>(
    collectMyReactionEventsFromRelations(room, matrixEventId, myMatrixUserId),
  )

  if (!shouldAllowTimelineFallback(room.roomId, matrixEventId, options)) {
    return [...eventsToRedact]
  }

  for (const event of collectMyReactionEventsFromTimeline(
    room,
    matrixEventId,
    myMatrixUserId,
  )) {
    eventsToRedact.add(event)
  }

  return [...eventsToRedact]
}

export async function removeMyMatrixReactions(
  matrixClient: MatrixClient,
  room: Room,
  matrixEventId: string,
  myMatrixUserId: string,
  options?: { keepEmoji?: string | null },
): Promise<void> {
  const keepEmoji = options?.keepEmoji
    ? normalizeEmojiForMatch(options.keepEmoji)
    : null

  const eventsToRedact = collectMyReactionEvents(room, matrixEventId, myMatrixUserId)
    .filter(event => {
      if (!keepEmoji) {
        return true
      }

      const key = normalizeEmojiForMatch(String(event.getRelation()?.key || ''))
      return key !== keepEmoji
    })

  if (eventsToRedact.length === 0) {
    return
  }

  await Promise.all(
    eventsToRedact.map(async event => {
      const eventId = event.getId()
      if (!eventId || event.isRedacted()) {
        return
      }

      try {
        await matrixClient.redactEvent(room.roomId, eventId)
      } catch (error) {
        // Уже снято / нет прав на повторный redact — не считаем ошибкой снятия
        if (event.isRedacted()) {
          return
        }
        const message = error instanceof Error ? error.message : String(error)
        if (/not found|already|unknown/i.test(message)) {
          return
        }
        throw error
      }
    }),
  )
}

export async function setMatrixExclusiveReaction(
  matrixClient: MatrixClient,
  room: Room,
  matrixEventId: string,
  emoji: string,
  myMatrixUserId: string,
): Promise<void> {
  const normalizedEmoji = normalizeEmojiForMatch(emoji)
  const myEvents = collectMyReactionEvents(room, matrixEventId, myMatrixUserId)
  const alreadySet = myEvents.some(
    event => normalizeEmojiForMatch(String(event.getRelation()?.key || '')) === normalizedEmoji,
  )

  // Уже стоит нужная реакция — не трогаем (remove+send давал redact без нового события)
  if (alreadySet && myEvents.length === 1) {
    return
  }

  if (myEvents.length > 0) {
    await removeMyMatrixReactions(matrixClient, room, matrixEventId, myMatrixUserId, {
      keepEmoji: alreadySet ? emoji : null,
    })
  }

  if (!alreadySet) {
    await sendMatrixReaction(matrixClient, room.roomId, matrixEventId, emoji)
  }
}
